import { defineMessages } from '../translate';

/**
 * User-facing text for API error codes. `errorText(err)` (shared/i18n/error-text.ts)
 * shows `code_<code>` when it exists, else the code's family text (`group_*`,
 * see error-groups.ts) — never raw English server copy in another language.
 * `src/__arch__/api-error-codes.spec.ts` keeps every API code covered.
 */
export default defineMessages('errors', {
  actionFailed: 'Action failed.',
  network: "Can't reach Projoint. Check your connection and try again.",
  code_transition_not_allowed: "That status isn't a valid next step for this task in the project's workflow.",
  code_mfa_assurance_required:
    'This needs a two-factor sign-in. Turn on 2FA in Account → Security if it is off, then sign out and sign in again with your code.',
  code_sensitive_browser_session_required: 'This can only be done from a normal signed-in browser session.',
  code_permission_denied: "You don't have permission to do that.",
  code_forbidden: "You don't have permission to do that.",
  code_not_authorised: "You don't have permission to do that.",
  code_unauthenticated: 'Your session has ended. Sign in again.',
  code_session_invalid: 'Your session has ended. Sign in again.',
  code_rate_limited: 'Too many requests. Wait a moment and try again.',
  code_internal_error: 'Something went wrong on our side. Please try again.',
  code_validation_failed: 'Some fields are not valid. Check them and try again.',
  code_team_not_found: 'Team not found.',
  code_project_not_found: 'Project not found.',
  code_task_not_found: 'Task not found.',
  code_user_not_found: 'User not found.',
  code_invitation_not_found: 'This invitation was not found or has expired.',
  code_invalid_credentials: 'Email or password is incorrect.',
  code_email_taken: 'That email is already in use.',
  code_username_taken: 'That username is already taken.',
  code_email_unchanged: 'That is already your email address.',
  code_invalid_code: "The code didn't match.",
  code_invalid_recovery_code: 'That recovery code is invalid or already used.',
  code_invalid_password: 'Current password is incorrect.',
  code_invalid_or_expired_token: 'This link is invalid or has expired.',
  code_enrolment_expired: 'Setup expired — start over.',
  code_account_banned: 'This account is blocked.',
  code_user_inactive: 'This account is not active.',
  code_no_password_set: 'Set a password first.',
  code_registration_invitation_invalid: 'Sign-up needs a valid team invitation.',
  code_reason_too_short: 'Please give a longer reason.',
  code_transition_requires_approval: 'This status change needs approval.',
  code_transition_role_gate: "Your role can't move the task to this status.",
  code_ai_unavailable: 'AI is unavailable right now. Try again later.',
  code_ai_rate_limited: 'AI request limit reached. Try again in a moment.',
  code_plan_feature_required: 'This is available on the Team plan. Upgrade in Billing to use it.',
  code_actor_not_team_member: "You're not a member of this team.",
  code_upgrade_required: 'This is part of the Personal plan. Upgrade in Billing to turn it on.',
  group_session: "Your sign-in is no longer valid. Sign in again.",
  group_concurrent: "Someone else changed this at the same moment. Refresh and try again.",
  group_not_found: "This item no longer exists or you don't have access to it.",
  group_forbidden: "You don't have permission to do that.",
  group_inactive: "This account or item isn't active, so this can't be done.",
  group_exists: "That already exists. Try a different name or value.",
  group_expired: "This has expired or is out of date. Refresh and try again.",
  group_limit: "That's over a limit. Make it smaller or try again later.",
  group_locked: "This can't be changed in its current state.",
  group_workflow: "This setup isn't valid. Check the statuses, transitions or rules and try again.",
  group_unavailable: "This isn't available right now. Try again later.",
  group_invalid: "Some of the input isn't valid. Check it and try again.",
  code_not_found: "This item no longer exists or you don't have access to it.",
  code_bad_request: "Some of the input isn't valid. Check it and try again.",
  code_unauthorized: "Your session has ended. Sign in again.",
  code_conflict: "Someone else changed this. Refresh and try again.",
  code_unprocessable: "Some of the input isn't valid. Check it and try again.",
  code_service_unavailable: "Projoint is briefly unavailable. Try again in a moment.",
  code_client_error: "That request couldn't be completed. Try again.",
  code_confirm_key_mismatch: "The project key you typed doesn't match.",
  code_confirm_slug_mismatch: "The name you typed doesn't match.",
  code_folder_name_conflict: "A folder with that name already exists here.",
  code_email_not_verified: "Confirm your email address first — use the link we sent you.",
  code_recent_sign_in_required: "For your security, sign in again and then retry.",
  code_project_archived: "This project is archived. Restore it in Settings › Projects first.",
  code_task_archived: "This task is archived. Restore it first.",
  code_edit_window_closed: "It's too late to edit this.",
  code_edit_window_expired: "It's too late to edit this.",
  code_invitation_expired: "This invitation has expired. Ask for a new one.",
  code_already_member: "This person is already a member.",
  code_project_key_taken: "That project key is already used in this team.",
  code_project_slug_taken: "That project address is already used in this team.",
  code_team_slug_taken: "That team address is taken. Try another one.",
  code_cannot_remove_last_owner: "A team needs at least one owner. Make someone else an owner first.",
  code_cannot_demote_last_owner: "A team needs at least one owner. Make someone else an owner first.",
  code_registration_closed: "Sign-up is closed. Ask a team to invite you.",
  code_linear_unauthorized: "Linear didn't accept the API key. Check it and try again.",
  code_invitation_inviter_lost_access: "The person who invited you no longer has access to this team. Ask for a new invitation.",
  code_FREE_TIER_PROJECTS: "You've reached your plan's project limit. Upgrade in Billing to add more.",
  code_FREE_TIER_USERS: "You've reached your plan's member limit. Upgrade in Billing to add more.",
  code_FREE_TIER_STORAGE: "Your plan's storage is full. Free up space or upgrade in Billing.",
  code_FREE_TIER_FILE_SIZE: "This file is bigger than your plan allows. Upgrade in Billing for bigger files.",
  code_system_project_locked: "Quick tasks can't be changed this way.",
  code_pin_limit_reached: "You've pinned the most items you can. Unpin one first.",
  code_saved_views_limit: "You've reached the limit of saved views. Delete one first.",
  code_personal_team_is_single_member: "Personal is just for you — you can't add people to it. Create a team to work together.",
  code_signup_rejected: "Sign-up could not be completed. Reload the page and try again.",
  code_signup_form_expired: "The sign-up form expired. Submit it again.",
  code_signup_too_fast: "That was quick — check the form and submit it again.",
  code_captcha_failed: "Please complete the CAPTCHA check.",
  code_captcha_unavailable: "We couldn't check the CAPTCHA right now. Try again in a minute.",
  code_email_domain_not_allowed: "Use a permanent email address — temporary mailboxes can't be used to sign up.",
  code_account_temporarily_locked: "Too many wrong passwords for this email. Wait a few minutes, or reset your password.",
  code_free_team_limit_reached: "You already own the most teams the Free plan allows. Upgrade a team or delete one you no longer need.",
  code_team_limit_reached: "You already own the most teams one person can own. Delete a team you no longer need first.",
  code_invitation_pending_limit: "This team has too many open invitations. Revoke some before sending more.",
  code_invitation_daily_limit: "This team has sent its daily maximum of invitations. Try again tomorrow.",
  code_invitation_resend_too_soon: "This invitation was just sent. Wait a couple of minutes before sending it again.",
  code_upload_daily_limit: "This team has uploaded its daily maximum. Try again in a few hours.",
} as const);
